import { useCallback } from 'react';
import { match } from 'variant';
import { deepEqual } from 'fast-equals';
import { updateAppManifestComponents } from '@wix/cli-app-manifest';
import { useMetasiteManagerClient } from '@wix/metasite-manager-client';
import { useARMClient } from '@wix/arm-client';
import { useDebugLog } from '@wix/cli-debug-log';
import { createAppManifest } from '../../app-manifest/create.js';
import { getComponentDevSourceBuilder } from '../../app-manifest/component-source.js';
import type {
  ChangeType,
  ProjectModelWatcher,
} from '../../app-manifest/project-model-watcher.js';
import { readAppConfig, saveAppConfig } from '../../files/app.config.js';
import { useProjectModel } from '../../model.js';
import type { ProjectModel } from '../../model.js';
import { useBlocksServicesClient } from '../../blocks/useBlocksServicesClient.js';
import type { BlocksComponentsData } from '../../blocks/blocks-services-client.js';
import { useSite } from '../../site/useSite.js';

export interface BackendUrls {
  tunnelUrl?: string;
  localUrl: string;
}

interface CreateAppManifestParams {
  model: ProjectModel;
  devServerUrl: string;
  backendServerUrl: BackendUrls | undefined;
  blocksComponentsData: BlocksComponentsData;
}

const createAppManifestWithBaseUrl = async ({
  model,
  devServerUrl,
  backendServerUrl,
  blocksComponentsData,
}: CreateAppManifestParams) => {
  const manifest = await createAppManifest({
    appId: model.config.appId,
    components: model.components,
    componentSourceBuilder: getComponentDevSourceBuilder(
      model,
      { devServerUrl },
      { backendDevServerUrl: backendServerUrl?.localUrl }
    ),
  });

  return updateAppManifestComponents({
    manifest,
    backendServerUrl: backendServerUrl?.tunnelUrl,
    baseUrl: devServerUrl,
    blocksComponentsData,
  });
};

interface CreateOptions {
  devServerUrl: string;
  backendServerUrl?: BackendUrls;
  projectModelWatcher: ProjectModelWatcher;
  onChange: (changeType: ChangeType) => void;
  onError: (e: unknown) => void;
}

export function useArmUpdater() {
  const { model } = useProjectModel();
  const blocksServicesClient = useBlocksServicesClient();

  const writeDebugLog = useDebugLog();
  const { createAppRelease, updateAppRelease } = useARMClient();
  const { site } = useSite();

  const metasiteManagerClient = useMetasiteManagerClient({ siteId: site.id });

  return useCallback(
    async ({
      backendServerUrl,
      devServerUrl,
      projectModelWatcher,
      onChange,
      onError,
    }: CreateOptions) => {
      const { projectId } = model.config;

      const blocksComponentsData =
        await blocksServicesClient.prepareBlocksComponentsData(model);

      const appManifest = await createAppManifestWithBaseUrl({
        model,
        devServerUrl,
        backendServerUrl,
        blocksComponentsData,
      });

      const appConfig = await readAppConfig(model.projectFolder, writeDebugLog);

      const createOrUpdateAppRelease = async (tag?: string) => {
        if (tag) {
          const updateState = await updateAppRelease(appManifest, tag);
          return match(updateState, {
            TagUpdated: () => tag,
            TagNotFound: ({ cause }) => {
              writeDebugLog(cause);
              return createAppRelease(appManifest, projectId);
            },
          });
        }
        return createAppRelease(appManifest, projectId);
      };

      const armTag = await createOrUpdateAppRelease(appConfig?.devArmTag);

      if (armTag !== appConfig?.devArmTag) {
        await saveAppConfig(model.projectFolder, { devArmTag: armTag }).catch(
          (error: unknown) => writeDebugLog(error)
        );
      }

      projectModelWatcher
        .onChange(async (newModel, changeType) => {
          try {
            if (
              changeType === 'model-updated' ||
              changeType === 'blocks-revision-changed' ||
              changeType === 'component-updated'
            ) {
              if (newModel.components.length === 0) {
                return;
              }
              const blocksComponentsData =
                await blocksServicesClient.prepareBlocksComponentsData(
                  newModel
                );

              const updatedAppManifest = await createAppManifestWithBaseUrl({
                model: newModel,
                devServerUrl,
                backendServerUrl,
                blocksComponentsData,
              });

              if (!deepEqual(appManifest, updatedAppManifest)) {
                await updateAppRelease(updatedAppManifest, armTag);
              }
            }

            if (changeType === 'blocks-revision-changed') {
              await blocksServicesClient.refreshBlocksInEditor({
                armTag,
                appDefId: newModel.config.appId,
              });
            }

            if (changeType === 'component-updated') {
              await metasiteManagerClient.distributeClientUpdate(
                newModel.config.appId
              );
            }

            onChange(changeType);
          } catch (e) {
            onError(e);
          }
        })
        .onError(onError);

      return armTag;
    },
    [
      createAppRelease,
      model,
      updateAppRelease,
      writeDebugLog,
      blocksServicesClient,
      metasiteManagerClient,
    ]
  );
}
